import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { fileKeys } from "@/lib/query";

export interface FileRecord {
  id: string;
  key: string;
  thumbnailKey: string | null;
  placeholder: string | null;
  contentType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  uploadedBy: string;
  status: "pending" | "uploaded" | "failed";
  createdAt: string;
  updatedAt: string;
}

export interface FileDetail extends FileRecord {
  /** Short-lived presigned GET URL for the primary object. */
  url: string;
  /** Short-lived presigned GET URL for the thumbnail, when present. */
  thumbnailUrl: string | null;
}

/**
 * Upload an image through the server pipeline — Bun.Image resizes, converts to
 * WebP, builds a thumbnail, and writes both to S3. Returns the file record.
 */
export function useUploadImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File): Promise<FileRecord> => {
      const body = new FormData();
      body.append("file", file);
      const res = await fetch("/api/files/image", { method: "POST", body });
      if (!res.ok) {
        const message = await res
          .json()
          .then((j: { error?: string }) => j.error)
          .catch(() => null);
        throw new Error(message ?? "Failed to upload image");
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fileKeys.all });
      toast.success("Image uploaded");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to upload image");
    },
  });
}

/** Resolve a file id to its metadata + a fresh presigned download URL. */
export function useFile(fileId: string | null) {
  return useQuery({
    queryKey: fileKeys.detail(fileId ?? ""),
    enabled: fileId !== null && fileId !== "",
    queryFn: async (): Promise<FileDetail> => {
      const res = await fetch(`/api/files/${fileId}`);
      if (!res.ok) throw new Error("Failed to fetch file");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useDeleteFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: string) => {
      const res = await fetch(`/api/files/${fileId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fileKeys.all });
      toast.success("File deleted");
    },
    onError: () => {
      toast.error("Failed to delete file");
    },
  });
}
